import React, { useEffect, useRef } from 'react';

interface UseHideGigObserverProps {
    targetRef: React.RefObject<HTMLElement>;
    callback: () => void;
}

const checkIfHidden = (element: HTMLElement | null | undefined): boolean => {
    if (!element) {
        return false;
    }
    const computedStyle = window.getComputedStyle(element);
    const display = computedStyle.display;
    return display === 'none';
};
/**
 * Observer hook to detect when a component is hidden through CSS styles
 * Checks every 5 seconds until the target element or its parent element is hidden, then stops.
 * Detects display: none applied via:
 * - Inline styles (style attribute)
 * - CSS classes
 * - External stylesheets
 * - Computed styles
 * - display: none !important
 */
const useHideGigObserver = ({ targetRef, callback }: UseHideGigObserverProps) => {
    const hasDetectedRef = useRef(false);

    useEffect(() => {
        if (!targetRef.current || hasDetectedRef.current) {
            return;
        }

        const checkTargetElementAndParent = () => {
            const targetElement = targetRef.current;
            if (hasDetectedRef.current) {
                return;
            }

            const parentElement = targetElement?.parentElement;

            if (checkIfHidden(targetElement) || checkIfHidden(parentElement)) {
                hasDetectedRef.current = true;
                callback();
            }
        };

        // Check every 5 seconds until detection
        const intervalId = setInterval(() => {
            checkTargetElementAndParent();

            // Stop interval if detection occurred
            if (hasDetectedRef.current) {
                clearInterval(intervalId);
            }
        }, 5000);

        // Cleanup function
        return () => {
            clearInterval(intervalId);
        };
    }, [targetRef, callback]);
};

export default useHideGigObserver;
